import React from 'react';
import type { EacProjectItem } from '../../../api';
import type {
  EacFirstRoundAnswer,
  EacSecondRoundAnswer,
  RecommendationDecision,
} from '../types';
import {
  buildRiskTypeText,
  formatDateTime,
  formatPercent,
  formatWanValue,
  getRecommendationStatusText,
  getRiskBadgeTone,
  getRiskTypeTone,
} from '../utils';

interface DetectionContentProps {
  project?: EacProjectItem;
  detectStarted: boolean;
  detecting: boolean;
  latestDetectTime: Date | null;
  firstRoundAnswer: EacFirstRoundAnswer | null;
  secondRoundAnswer: EacSecondRoundAnswer | null;
  secondRoundPending: boolean;
  recommendationDecision: RecommendationDecision;
  adoptSubmitting: boolean;
  onRejectRecommendation: () => void;
  onRetryRecommendation: () => void;
  onAdoptRecommendation: () => void;
}

const DetectingIcon: React.FC = () => {
  return (
    <svg
      viewBox="0 0 1184 1024"
      className="h-[44px] w-[50px]"
      aria-hidden="true"
    >
      <path
        d="M982.78577 764.55339l183.898823 184.495898a42.392261 42.392261 0 1 1-59.70741 59.70741l-185.092972-184.794435a468.106097 468.106097 0 0 1-298.537052 108.070413A465.419264 465.419264 0 0 1 268.685142 767.53876c-44.482021-52.243984 22.987353-100.905524 74.037188-44.780558a380.634741 380.634741 0 0 0 549.606713 13.434168 388.098167 388.098167 0 0 0 82.9933-123.892877 383.620112 383.620112 0 0 0 0-288.983866v-1.791222A378.246445 378.246445 0 0 0 895.61295 199.124214l-2.089759-2.08976a383.321575 383.321575 0 0 0-123.892876-82.396226 367.200574 367.200574 0 0 0-145.686082-29.853705 381.828889 381.828889 0 0 0-347.795665 228.082307 42.093724 42.093724 0 0 1-39.108354 25.077113 42.690798 42.690798 0 0 1-38.809817-59.707411A470.494394 470.494394 0 0 1 623.645696 0a469.001708 469.001708 0 0 1 329.883442 136.431433l2.388297 2.089759a473.479764 473.479764 0 0 1 98.517227 149.268526v2.388296a463.926579 463.926579 0 0 1 0 353.766407 470.494394 470.494394 0 0 1-71.94743 119.414821z m-957.408326-179.122231a25.375649 25.375649 0 0 1-25.375649-25.674187 25.674186 25.674186 0 0 1 25.375649-25.674186h188.973954L328.392552 343.914684a25.674186 25.674186 0 0 1 34.928835-9.254649 25.375649 25.375649 0 0 1 8.359037 8.657575l109.861636 176.435397 54.63228-57.617651a25.972724 25.972724 0 0 1 36.42152 0 29.853705 29.853705 0 0 1 5.373667 7.761964L640.960845 597.074104l78.813782-332.271739a25.674186 25.674186 0 0 1 29.853705-18.807834 25.375649 25.375649 0 0 1 11.941482 6.567815l116.130913 117.623598a25.674186 25.674186 0 1 1-36.720057 35.824446l-83.590375-84.485985-83.291837 356.751777a22.987353 22.987353 0 0 1-13.732704 16.718075 24.181501 24.181501 0 0 1-33.43615-10.747334l-78.813782-160.01586-51.64691 54.035206a15.822464 15.822464 0 0 1-5.970741 4.776593 25.375649 25.375649 0 0 1-35.227372-7.761963l-105.085042-168.374897-98.815764 164.792452a24.480038 24.480038 0 0 1-22.390279 13.732705H25.377444z"
        fill="#f59e0b"
      />
    </svg>
  );
};

interface FadeInBlockProps {
  children: React.ReactNode;
  className?: string;
  delayMs?: number;
}

const FadeInBlock: React.FC<FadeInBlockProps> = ({
  children,
  className,
  delayMs = 0,
}) => {
  const [entered, setEntered] = React.useState(false);

  React.useEffect(() => {
    // 区块挂载后触发一次渐入，与日志面板保持同款入场节奏。
    let rafId = 0;
    const timerId = window.setTimeout(() => {
      rafId = window.requestAnimationFrame(() => {
        setEntered(true);
      });
    }, delayMs);

    return () => {
      window.clearTimeout(timerId);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [delayMs]);

  return (
    <div
      className={`${className || ''} transition-all duration-300 ease-out ${
        entered ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
    >
      {children}
    </div>
  );
};

const AnalyzingSpinner: React.FC = () => {
  return (
    <span className="inline-block h-[14px] w-[14px] animate-spin rounded-full border-[2px] border-solid border-[#129bfe] border-t-transparent" />
  );
};

const AnimatedEllipsis: React.FC = () => {
  const [dotCount, setDotCount] = React.useState(1);

  React.useEffect(() => {
    const timer = window.setInterval(() => {
      setDotCount(prev => (prev % 3) + 1);
    }, 400);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  return (
    <span className="inline-block w-[24px] text-left">
      {'.'.repeat(dotCount)}
    </span>
  );
};

const DetectionContent: React.FC<DetectionContentProps> = ({
  project,
  detectStarted,
  detecting,
  latestDetectTime,
  firstRoundAnswer,
  secondRoundAnswer,
  secondRoundPending,
  recommendationDecision,
  adoptSubmitting,
  onRejectRecommendation,
  onRetryRecommendation,
  onAdoptRecommendation,
}) => {
  if (!detectStarted || !project) {
    return null;
  }

  const warning = firstRoundAnswer?.EAC预警结果;
  const riskLevel = warning?.风险等级 || '--';
  const riskTypeText = buildRiskTypeText(firstRoundAnswer);
  const riskTone = getRiskBadgeTone(warning?.风险等级);
  const riskTypeTone = getRiskTypeTone(warning?.风险等级);
  const eacBudgetRatio =
    typeof warning?.['EAC/Budget'] === 'number'
      ? warning['EAC/Budget']
      : project.bac > 0
        ? (project.eac / project.bac) * 100
        : undefined;
  const recommendation = secondRoundAnswer?.详细纠偏方案;
  const statusText = getRecommendationStatusText(recommendationDecision);
  const hasRecommendation = !!(
    recommendation &&
    (recommendation.方案 ||
      recommendation.详细建议 ||
      recommendation.数据更新要求)
  );
  const showRecommendationBlock =
    !detecting && !!warning && warning.风险等级 !== '无风险';
  const showAiAnalyzingStatus =
    secondRoundPending && !statusText && !hasRecommendation;
  const showActionButtons = !statusText && hasRecommendation;
  const recommendationEffectText =
    buildRecommendationEffectText(recommendation);
  const metricItems = [
    {
      label: 'Budget',
      value: formatWanValue(project.bac),
      color: '#129bfe',
    },
    {
      label: 'Actual',
      value: formatWanValue(project.ac),
      color: '#129bfe',
    },
    {
      label: 'Forecast',
      value: formatWanValue(project.forecast),
      color: '#129bfe',
    },
    {
      label: 'EAC',
      value: formatWanValue(project.eac),
      color: '#129bfe',
    },
    {
      label: 'EAC/Budget',
      value: formatPercent(eacBudgetRatio),
      color:
        typeof eacBudgetRatio === 'number' && eacBudgetRatio > 115
          ? '#ef4444'
          : typeof eacBudgetRatio === 'number' && eacBudgetRatio > 110
            ? '#f59e0b'
            : typeof eacBudgetRatio === 'number' && eacBudgetRatio > 100
              ? '#eab308'
              : '#129bfe',
    },
  ];

  return (
    <div className="flex h-full w-full min-w-0 flex-col overflow-y-auto px-6 py-6">
      <div className="mx-auto flex w-full max-w-[760px] flex-col">
        <FadeInBlock
          key={`project-${project.code}-${latestDetectTime?.getTime() || 0}`}
          className="mb-3"
        >
          <div className="text-[14px] font-semibold leading-6 text-[#1f2937]">
            项目：{project.name}（ID: {project.code}）
          </div>
        </FadeInBlock>

        <FadeInBlock
          key={`summary-${project.code}-${latestDetectTime?.getTime() || 0}`}
        >
          <section className="rounded-[4px] border border-solid border-[#d6dbe2] bg-white">
            {detecting ? (
              <div className="flex h-[230px] flex-col items-center justify-center">
                <div className="relative right-[8px] animate-pulse">
                  <DetectingIcon />
                </div>
                <div className="mt-5 flex items-center text-[16px] leading-6 text-[#4b5563]">
                  <span>风险预警分析中</span>
                  <AnimatedEllipsis />
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 border-0 border-b border-dashed border-[#d6dbe2] px-4 py-3 text-[12px] leading-5 text-[#1f2937]">
                  <span>最近检测时间：{formatDateTime(latestDetectTime)}</span>
                  <span className="inline-flex items-center rounded-full bg-[#eef1f6] px-2 py-[1px] text-[11px] text-[#6b7280]">
                    手动
                  </span>
                </div>

                <div className="flex items-center px-4 py-5">
                  {metricItems.map(metric => (
                    <div
                      key={metric.label}
                      className="flex min-w-0 flex-1 flex-col items-center justify-center"
                    >
                      <div
                        className="text-[15px] leading-6"
                        style={{ color: metric.color }}
                      >
                        {metric.value}
                      </div>
                      <div className="text-[12px] leading-5 text-[#6b7280]">
                        {metric.label}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-0 border-t border-dashed border-[#d6dbe2] px-4 py-7">
                  <div className="flex items-center justify-center gap-6">
                    <RiskPill
                      title={riskLevel}
                      subtitle="风险等级"
                      bgColor={riskTone.bgColor}
                      textColor={riskTone.textColor}
                    />
                    <RiskPill
                      title={riskTypeText}
                      subtitle="风险类型"
                      bgColor={riskTypeTone.bgColor}
                      textColor={riskTypeTone.textColor}
                    />
                  </div>
                </div>
              </>
            )}
          </section>
        </FadeInBlock>

        {showRecommendationBlock && (
          <FadeInBlock
            key={`recommendation-${recommendationDecision || 'none'}`}
            className="mt-10"
          >
            <section className="flex min-h-[330px] flex-1 flex-col">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[18px] font-semibold leading-6 text-[#1f2937]">
                  纠偏方案（AI推荐）
                </div>
                {statusText ? (
                  <span
                    className={`inline-flex h-7 items-center rounded-full px-4 text-[12px] leading-6 ${
                      recommendationDecision === 'adopted'
                        ? 'bg-[#e4f9df] text-[#39b54a]'
                        : 'bg-[#ffe3e6] text-[#ff4d5f]'
                    }`}
                  >
                    {statusText}
                  </span>
                ) : showAiAnalyzingStatus ? (
                  <span className="inline-flex h-7 items-center gap-2 rounded-full bg-[#eef4ff] px-4 text-[12px] leading-6 text-[#129bfe]">
                    <AnalyzingSpinner />
                    AI分析中...
                  </span>
                ) : null}
              </div>

              <div className="min-h-[156px] rounded-[4px] border border-solid border-[#129bfe] bg-[#e8f2ff] px-4 py-4 text-[14px] leading-8 text-[#1f2937]">
                {hasRecommendation ? (
                  <>
                    <div>推荐方案：{recommendation.方案 || '暂无推荐方案'}</div>
                    <div>
                      详细建议：{recommendation.详细建议 || '暂无推荐方案'}
                    </div>
                    <div>{recommendationEffectText}</div>
                  </>
                ) : secondRoundPending ? (
                  <div>检测到风险，AI正在分析纠偏方案，请稍候...</div>
                ) : (
                  <div>暂无推荐方案</div>
                )}
              </div>

              {showActionButtons && (
                <div className="mt-4 flex items-center justify-center gap-8">
                  <button
                    type="button"
                    onClick={onRejectRecommendation}
                    className="h-8 min-w-[90px] cursor-pointer rounded-[4px] border border-solid border-[#b9bec7] bg-white px-4 text-[13px] text-[#374151] disabled:cursor-not-allowed disabled:border-[#d1d5db] disabled:text-[#9ca3af]"
                  >
                    放弃推荐
                  </button>
                  <button
                    type="button"
                    onClick={onRetryRecommendation}
                    className="h-8 min-w-[90px] cursor-pointer rounded-[4px] border border-solid border-[#129bfe] bg-white px-4 text-[13px] text-[#129bfe] disabled:cursor-not-allowed disabled:border-[#bfd0ff] disabled:text-[#9cb3ff]"
                  >
                    重新推荐
                  </button>
                  <button
                    type="button"
                    onClick={onAdoptRecommendation}
                    disabled={adoptSubmitting || !recommendation}
                    className="h-8 min-w-[90px] cursor-pointer rounded-[4px] border-none bg-[#129bfe] px-4 text-[13px] text-white disabled:cursor-not-allowed disabled:bg-[#b8c8ff]"
                  >
                    {adoptSubmitting ? '采纳中...' : '采纳方案'}
                  </button>
                </div>
              )}
            </section>
          </FadeInBlock>
        )}
      </div>
    </div>
  );
};

interface RiskPillProps {
  title: string;
  subtitle: string;
  bgColor: string;
  textColor: string;
}

const RiskPill: React.FC<RiskPillProps> = ({
  title,
  subtitle,
  bgColor,
  textColor,
}) => {
  return (
    <div
      className="flex h-[112px] w-[188px] shrink-0 flex-col items-center justify-center rounded-[50%]"
      style={{ backgroundColor: bgColor }}
    >
      <div
        className="text-[16px] font-semibold leading-6"
        style={{ color: textColor }}
      >
        {title}
      </div>
      <div className="mt-1 text-[14px] leading-6 text-[#374151]">
        {subtitle}
      </div>
    </div>
  );
};

function buildRecommendationEffectText(
  recommendation?: { 数据更新要求?: string } | null
): string {
  const rawValue = recommendation?.数据更新要求;
  if (!rawValue) {
    return '采取此方案后，暂无可展示的结果数据。';
  }

  try {
    const parsed = JSON.parse(rawValue) as {
      project?: {
        eac?: number;
      };
    };
    if (typeof parsed.project?.eac === 'number') {
      return `采取此方案后，EAC预计调整为${formatWanValue(parsed.project.eac)}。`;
    }
  } catch {
    return '采取此方案后，可按推荐结果回写项目数据。';
  }

  return '采取此方案后，可按推荐结果回写项目数据。';
}

export default DetectionContent;
